import {FC, Fragment, useState} from 'react';
import {Link} from 'react-router-dom';

interface Crumb {
  id: string;
  name: string;
  tooltip?: string;
  action?: () => void;
  link?: string;
}

interface Props {
  crumbs: Crumb[];
  maxCrumbsToDisplay: number;
}

const Breadcrumb: FC<Props> = ({crumbs, maxCrumbsToDisplay}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const MAX_CRUMBS_TO_DISPLAY = maxCrumbsToDisplay;

  const crumbItem = (crumb: Crumb, index: number) => {
    const {action, link, name, tooltip} = crumb;
    // The last element (which is the current page) should not have a link
    const isLink = !!link && index < crumbs.length - 1;

    return (
      <li className="content-center flex items-center" onMouseOver={() => setIsDropdownOpen(false)}>
        {isLink ? (
          <Link className="font-medium hover:underline" onClick={action} to={link} data-tooltip-id="main-tooltip" data-tooltip-content={tooltip}>
            {name}
          </Link>
        ) : (
          <span className="font-normal">{name}</span>
        )}
      </li>
    );
  };

  const ellipsisBreadcrumbs = crumbs.slice(1, -MAX_CRUMBS_TO_DISPLAY + 1);
  const remainingBreadcrumbs = crumbs?.length > MAX_CRUMBS_TO_DISPLAY ? crumbs.slice(-MAX_CRUMBS_TO_DISPLAY + 1) : crumbs.slice(1);

  return (
    <ol className="flex">
      {crumbs?.length > 0 && (
        <>
          {/* Display first crumb */}
          {crumbItem(crumbs[0], 0)}
          {crumbs.length > 1 && <span className="mx-2">{'>'}</span>}

          {/* Display ellipsis */}
          {crumbs.length > MAX_CRUMBS_TO_DISPLAY ? (
            <>
              <button onMouseOver={() => setIsDropdownOpen(true)}>...</button>
              {isDropdownOpen && (
                <div
                  onMouseOver={() => setIsDropdownOpen(true)}
                  onMouseOut={() => setIsDropdownOpen(false)}
                  className="bg-transparent border-b border-x border-gray-300 shadow-lg absolute top-16 select-none"
                >
                  <ul className="max-h-60 overflow-auto w-full">
                    {ellipsisBreadcrumbs.map(({id, name, action, link, tooltip}) => (
                      <li
                        key={id}
                        className="flex p-2 bg-white hover:bg-gray-100 cursor-pointer"
                        onClick={() => setIsDropdownOpen(false)}
                        data-tooltip-id={`product-crumb-tooltip-${id}`}
                        data-tooltip-content={tooltip}
                      >
                        <Link className="font-medium block w-full" onClick={action} to={link!}>
                          {name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <span className="mx-2">{'>'}</span>
            </>
          ) : null}

          {/* Display last elements */}
          {remainingBreadcrumbs.map((crumb, index) => (
            <Fragment key={crumb.id}>
              {crumbItem(crumb, index + 1)}
              {index < remainingBreadcrumbs.length - 1 && <span className="mx-2">{'>'}</span>}
            </Fragment>
          ))}
        </>
      )}
    </ol>
  );
};

export default Breadcrumb;
